import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigationType, useSearchParams } from "react-router-dom";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReceivedMatchingItem from "components/expert/received_matching_item";
import MatchingFilter from "components/expert/matching_filter";
import { useUser } from "util/context/user_context";
import { getRequiredApi } from "api/match";
import { parseRegionCsv, addressMatchesAny } from "util/constants/regions";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  amber800: "#92400E",
};

const ITEMS_PER_PAGE = 5;
const PAGE_GROUP = 5;

function SkeletonCard() {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: 20,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 12,
          background: "#F3F4F6",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 18, width: "60%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "40%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "50%", background: "#F3F4F6", borderRadius: 6 }} />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        background: "#fff",
        borderRadius: 16,
        border: `1px dashed ${C.line}`,
        gap: 12,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 9999,
          background: C.greenBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 34,
        }}
      >
        🤝
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
        받은 요청이 없어요
      </div>
      <div style={{ fontSize: 13, color: C.sub, textAlign: "center" }}>
        새로운 수리 요청이 들어오면 여기에 표시됩니다.
      </div>
    </div>
  );
}

function SimplePagenation({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const startPage = Math.floor((page - 1) / PAGE_GROUP) * PAGE_GROUP + 1;
  const endPage = Math.min(startPage + PAGE_GROUP - 1, totalPages);
  const numbers = [];
  for (let i = startPage; i <= endPage; i++) numbers.push(i);

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const btnBase = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    fontSize: 14,
  };

  return (
    <div style={{ display: "flex", gap: 20, margin: "24px 0", justifyContent: "center", alignItems: "center" }}>
      <button
        type="button"
        onClick={() => canPrev && onChange(page - 1)}
        style={{ ...btnBase, cursor: canPrev ? "pointer" : "default", color: canPrev ? C.sub : "#c3c3c3" }}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <span>이전</span>
      </button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {numbers.map((n) => {
          const active = n === page;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                width: 32,
                height: 32,
                cursor: "pointer",
                borderRadius: 9999,
                backgroundColor: active ? C.green : "transparent",
                color: active ? "#fff" : C.body,
                fontWeight: active ? 700 : 500,
                border: "none",
                transition: "all 0.15s ease",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => canNext && onChange(page + 1)}
        style={{ ...btnBase, cursor: canNext ? "pointer" : "default", color: canNext ? C.sub : "#c3c3c3" }}
      >
        <span>다음</span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
}

const ExpertReceivedScreen = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const { userData } = useUser();

  const category = searchParams.get("category") || null;
  // 다중 region (CSV). URL 우선, 없으면 마이페이지 단일 region 으로 폴백.
  const regions = parseRegionCsv(
    searchParams.get("region") ??
      (userData?.region && userData.region !== "전국" ? userData.region : ""),
  );
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [page, setPage] = useState(() => {
    if (navigationType === "POP") {
      return Number(sessionStorage.getItem(`page_${location.pathname}`) ?? 1);
    }
    return 1;
  });

  // 모든 공개 요청을 한 번에 가져와 클라이언트 단에서 필터/페이지네이션
  const fetchList = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const list = await getRequiredApi();
      setAllItems(Array.isArray(list) ? list : []);
    } catch {
      if (!silent) setAllItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // 자동 갱신 — 60초마다 새 요청 반영 (백그라운드 탭이면 브라우저가 알아서 지연시킴)
  useEffect(() => {
    const timer = setInterval(() => fetchList({ silent: true }), 60000);
    return () => clearInterval(timer);
  }, [fetchList]);

  // 수동 새로고침
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await fetchList({ silent: true });
    setRefreshing(false);
  };

  // 카테고리 또는 지역 변경 시 첫 페이지로
  const regionUrlParam = searchParams.get("region") || "";
  useEffect(() => {
    setPage(1);
  }, [category, regionUrlParam]);

  // 카테고리 + 다중 지역 필터 (regions 배열의 ANY 매칭)
  const filtered = useMemo(() => {
    let list = allItems;
    if (category) {
      list = list.filter((it) => {
        if (it?.selectList === category) return true;
        const names = it?.categoryListName ?? [];
        return names[0] === category;
      });
    }
    if (regions.length > 0) {
      list = list.filter((it) => addressMatchesAny(it?.address, regions));
    }
    return list;
  }, [allItems, category, regions]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentItems = useMemo(
    () => filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page],
  );

  useEffect(() => {
    if (page > totalPages) setPage(Math.max(1, totalPages));
  }, [totalPages, page]);

  useEffect(() => {
    sessionStorage.setItem(`page_${location.pathname}`, page);
    window.scrollTo({ top: 0 });
  }, [page, location.pathname]);

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 1100,
        margin: "0 auto",
        padding: "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            받은 요청
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            농가에서 등록한 수리 요청 중 매칭 대기 중인 건들을 확인하세요.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              padding: "10px 18px",
              borderRadius: 10,
              background: "#fff",
              border: `1px solid ${C.line}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              fontWeight: 600,
              color: C.body,
            }}
          >
            <span style={{ color: C.amber800 }}>●</span>
            <span>매칭 대기 중</span>
            <span style={{ fontWeight: 800, color: C.ink }}>{filtered.length}</span>
          </div>
          {/* 수동 새로고침 (60초 자동 갱신도 동작) */}
          <button
            type="button"
            onClick={handleRefresh}
            aria-label="목록 새로고침"
            title="목록 새로고침 (60초마다 자동 갱신)"
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: "#fff",
              border: `1px solid ${C.line}`,
              cursor: refreshing ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 17,
              color: C.body,
              opacity: refreshing ? 0.5 : 1,
            }}
          >
            <span
              style={{
                display: "inline-block",
                animation: refreshing ? "spin 0.8s linear infinite" : "none",
              }}
            >
              ↻
            </span>
          </button>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>

      {/* 내 지역 미설정 유도 배너 */}
      {!userData?.region && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            padding: "12px 16px",
            borderRadius: 12,
            background: C.greenBg,
            border: "1px solid #A7F3D0",
          }}
        >
          <div style={{ fontSize: 13, color: C.body }}>
            <b style={{ color: C.greenDark }}>내 지역을 설정</b>하면 우리 지역
            요청만 모아볼 수 있어요. 지금은 전국 요청이 표시됩니다.
          </div>
          <Link
            to="/mypage"
            style={{
              flexShrink: 0,
              padding: "8px 14px",
              borderRadius: 9999,
              background: C.greenDark,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            내 지역 설정하기 →
          </Link>
        </div>
      )}

      {/* Filter — 내 지역 + 카테고리 칩 */}
      <MatchingFilter address={userData?.address} userRegion={userData?.region} />

      {/* List */}
      <div className="list" style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : currentItems.length > 0 ? (
          currentItems.map((item, index) => (
            <ReceivedMatchingItem key={item?.docId || index} item={item} />
          ))
        ) : (
          <EmptyState />
        )}
      </div>

      <SimplePagenation page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
};

export default ExpertReceivedScreen;
